import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
  AbstractControl,
  FormControl,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin, from, map, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, concatMap, last, take, tap } from 'rxjs/operators';
import { Modal } from '@spirit-testing/component-library';
import { ModalEscapeGuardDirective } from '../../components/modal-escape-guard/modal-escape-guard.directive';

import { TenantUserService } from '../../services/tenant-user.service';
import { UserProjectPermissionsService } from '../../services/user-project-permissions.service';
import {
  ProjectPermissionEntryDTO,
  UserProjectPermissionsDTO,
  effectivePermissions,
  overridesFor,
  samePermissions,
} from '../../models/user-project-permissions.model';
import { CustomerDetailService } from '../../services/customer-detail.service';
import { AccountService } from '../../account/account.service';
import { MAIL_NOT_DELIVERED_SNACKBAR } from '../../shared/invitation-mail-snackbar';
import {
  CustomerDTO,
  CustomerUserDTO,
  InvitationDTO,
  RoleDTO,
  TenantUserStatus,
  UpdateCustomerUserRequest,
} from '../../models/customer.model';
import { FIELD_LENGTHS } from '../../shared/field-lengths';
import { ASSIGNABLE_TENANT_ROLE_CODES } from '../../shared/tenant-roles';
import { isAgentAccount, visibleAddress } from '../../shared/account-kind';
import { warnBeforeUnload } from '../../shared/forms/warn-before-unload';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { StatusPillComponent } from '../../components/status-pill/status-pill.component';
import { SectionTitleComponent } from '../../components/section-title/section-title.component';
import {
  EinzelrechteMatrixComponent,
  EinzelrechteMatrixState,
} from '../../components/einzelrechte-matrix/einzelrechte-matrix.component';

interface BlockingProject {
  id: string;
  name: string;
}

type TabId = 'daten' | 'rolle' | 'praeferenzen';
type UserStatus = TenantUserStatus;
/** Grund, warum ein Mandanten-Admin die Rolle nicht ändern darf; Reihenfolge wie „Rollen“ in der Verwaltung. */
type TenantRoleLock = 'eigene' | 'einladung' | 'platformAdmin' | 'agent';

interface TabDef {
  id: TabId;
  labelKey: string;
  icon: string;
}

const TABS: readonly TabDef[] = [
  { id: 'daten', labelKey: 'userEdit.tabs.stammdaten', icon: 'group' },
  { id: 'rolle', labelKey: 'userEdit.tabs.berechtigungen', icon: 'shield' },
  { id: 'praeferenzen', labelKey: 'userEdit.tabs.praeferenzen', icon: 'settings' },
];

const FIRST_LAST_NAME_MAX = 100;

/** Prüft den getrimmten Wert, weil auch das Backend vor der Prüfung trimmt: Leerzeichen am Rand sind kein Formatfehler. */
function trimmedEmailValidator(control: AbstractControl): ValidationErrors | null {
  const trimmed = new FormControl(String(control.value ?? '').trim());
  return Validators.required(trimmed) ?? Validators.email(trimmed);
}

/** Backend error codes on the user PUT that belong to the email field, mapped to its error key. */
const EMAIL_ERROR_BY_CODE: Readonly<Record<string, string>> = {
  EMAIL_ALREADY_IN_USE: 'emailInUse',
  EMAIL_DOMAIN_NOT_ALLOWED: 'domainNotAllowed',
  INVALID_EMAIL: 'email',
};

interface PendingChanges {
  name: boolean;
  status: boolean;
  role: boolean;
  /** Normalized new address, or null when the address is unchanged. */
  email: string | null;
  /** New value, or null when unchanged; an invitation's preferences never change here. */
  language: string | null;
  timezone: string | null;
}

class PermissionSaveError {
  constructor(
    readonly status: number,
    readonly code: string | null,
  ) {}

  get projectArchived(): boolean {
    return this.status === 403 && this.code === 'PROJECT_ARCHIVED';
  }

  get projectGone(): boolean {
    return this.status === 404 || this.projectArchived;
  }
}

const PERMISSION_SAVE_ERROR_KEYS: Readonly<Record<number, string>> = {
  403: 'userEdit.berechtigungen.saveErrorForbidden',
  409: 'userEdit.berechtigungen.saveErrorAdmin',
};

interface LanguageOption {
  code: string;
  label: string;
}

const LANGUAGE_CODES: readonly string[] = ['de', 'en'];
/** Labels also for codes the invitation accepts beyond the offered ones, so a stored value stays readable. */
const LANGUAGE_LABELS: Readonly<Record<string, string>> = {
  de: 'Deutsch',
  en: 'English',
  fr: 'Français',
  es: 'Español',
};
const TIMEZONE_OPTIONS: readonly string[] = ['Europe/Berlin', 'Europe/Vienna', 'Europe/Zurich', 'UTC'];
/** Column defaults of the backend, used while a user carries no preferences. */
const DEFAULT_LANGUAGE = 'de';
const DEFAULT_TIMEZONE = 'Europe/Berlin';

/** A stored value outside the offered list stays selectable, so opening and saving never changes it silently. */
function withStoredValue(options: readonly string[], stored: string): string[] {
  return options.includes(stored) ? [...options] : [...options, stored];
}

const STATUS_OPTIONS: readonly UserStatus[] = ['ACTIVE', 'PENDING', 'INACTIVE'];

@Component({
  selector: 'app-user-edit-page',
  imports: [
    TranslatePipe,
    MatIconModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    DatePipe,
    Modal,
    ModalEscapeGuardDirective,
    AvatarComponent,
    StatusPillComponent,
    SectionTitleComponent,
    EinzelrechteMatrixComponent,
  ],
  templateUrl: './user-edit-page.component.html',
  styleUrl: './user-edit-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserEditPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly tenantUserService = inject(TenantUserService);
  private readonly permissionsService = inject(UserProjectPermissionsService);
  private readonly customerDetailService = inject(CustomerDetailService);
  private readonly accountService = inject(AccountService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly injector = inject(Injector);
  private readonly emailInput = viewChild<ElementRef<HTMLInputElement>>('emailInput');

  readonly user = signal<CustomerUserDTO | null>(null);
  readonly customer = signal<CustomerDTO | null>(null);
  readonly roles = signal<RoleDTO[]>([]);
  readonly currentUserRole = signal<string | null>(null);
  private readonly currentUserId = signal<string | null>(null);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);

  readonly activeTab = signal<TabId>('daten');
  readonly tabs = TABS;

  readonly showDeleteDialog = signal(false);
  readonly deleting = signal(false);
  private readonly deleted = signal(false);
  readonly showDeactivateConfirm = signal(false);
  private readonly deactivateConfirm$ = new Subject<boolean>();
  readonly blockingProjects = signal<BlockingProject[]>([]);

  readonly permissions = signal<UserProjectPermissionsDTO | null>(null);
  readonly permissionsState = signal<EinzelrechteMatrixState>('loading');
  readonly selectedPermissionProject = signal<string | null>(null);
  readonly checkedPermissions = signal<Record<string, string[]>>({});
  readonly languageOptions = computed<LanguageOption[]>(() =>
    withStoredValue(LANGUAGE_CODES, this.storedLanguage()).map((code) => ({
      code,
      label: LANGUAGE_LABELS[code] ?? code,
    })),
  );
  readonly timezoneOptions = computed(() => withStoredValue(TIMEZONE_OPTIONS, this.storedTimezone()));
  readonly statusOptions = STATUS_OPTIONS;

  private readonly customerId = signal('');
  private readonly userId = signal('');
  /** Route `/settings/benutzer/:userId/bearbeiten` ohne customerId: Einstieg über „Rollen“ in der Verwaltung. */
  private fromVerwaltung = false;

  readonly displayName = computed(() => this.user()?.displayName ?? '');
  private readonly storedLanguage = computed(() => this.user()?.language ?? DEFAULT_LANGUAGE);
  private readonly storedTimezone = computed(() => this.user()?.timezone ?? DEFAULT_TIMEZONE);
  readonly isAgent = computed(() => {
    const user = this.user();
    return !!user && isAgentAccount(user);
  });
  readonly email = computed(() => {
    const user = this.user();
    return user ? visibleAddress(user) : '';
  });
  readonly isPlatformAdmin = computed(() => this.currentUserRole() === 'PLATFORM_ADMIN');
  /** Mandanten-Admins ändern nur Rolle und Einzelrechte; Stammdaten und Präferenzen pflegt ein Platform-Admin. */
  readonly masterDataReadOnly = computed(() => this.currentUserRole() !== null && !this.isPlatformAdmin());

  readonly tenantRoleLock = computed<TenantRoleLock | null>(() => {
    const user = this.user();
    if (!user || !this.masterDataReadOnly()) return null;
    if (user.id === this.currentUserId()) return 'eigene';
    if (this.showInvitationInfo() || user.status === 'PENDING') return 'einladung';
    if (user.role === 'PLATFORM_ADMIN') return 'platformAdmin';
    return this.isAgent() ? 'agent' : null;
  });

  /** Mandanten-Admins vergeben nur Benutzer und Mandanten-Admin; die gespeicherte Rolle bleibt sichtbar. */
  readonly roleOptions = computed<RoleDTO[]>(() => {
    if (this.isPlatformAdmin()) return this.roles();
    const storedRoleId = this.user()?.roleId;
    return this.roles().filter((role) => ASSIGNABLE_TENANT_ROLE_CODES.includes(role.code) || role.id === storedRoleId);
  });

  /** Übersetzungsschlüssel des Grunds, warum eine Rolle nicht wählbar ist; `null`, wenn sie wählbar ist. */
  roleLockReason(role: RoleDTO): string | null {
    if (role.code === 'PLATFORM_ADMIN') {
      return 'userEdit.stammdaten.platformAdminTooltip';
    }
    if (role.code === 'TENANT_ADMIN' && this.isAgent()) {
      return 'userEdit.stammdaten.agentTenantAdminTooltip';
    }
    return null;
  }
  readonly fieldLengths = { FIRST_LAST_NAME_MAX, EMAIL_MAX: FIELD_LENGTHS.EMAIL_MAX };

  // Als Validator statt setErrors(): Beim Tab-Wechsel meldet sich das Feld neu an und liefe dabei
  // erneut durch die Validatoren, ein gesetzter Fehler wäre weg. Er gilt nur für die abgelehnte Adresse.
  private rejectedEmail: { value: string; errorKey: string } | null = null;
  private readonly rejectedEmailValidator = (control: AbstractControl): ValidationErrors | null =>
    this.rejectedEmail && control.value === this.rejectedEmail.value ? { [this.rejectedEmail.errorKey]: true } : null;

  readonly form = this.fb.group({
    firstName: this.fb.control('', [Validators.required, Validators.maxLength(FIRST_LAST_NAME_MAX)]),
    lastName: this.fb.control('', [Validators.required, Validators.maxLength(FIRST_LAST_NAME_MAX)]),
    email: this.fb.control('', [
      trimmedEmailValidator,
      Validators.maxLength(FIELD_LENGTHS.EMAIL_MAX),
      this.rejectedEmailValidator,
    ]),
    roleId: this.fb.control(''),
    status: this.fb.control<UserStatus>('ACTIVE'),
    language: this.fb.control(DEFAULT_LANGUAGE),
    timezone: this.fb.control(DEFAULT_TIMEZONE),
  });

  private readonly roleIdSignal = toSignal(this.form.controls.roleId.valueChanges, {
    initialValue: this.form.controls.roleId.value,
  });

  private readonly changedPermissionProjects = computed<ProjectPermissionEntryDTO[]>(() => {
    const checked = this.checkedPermissions();
    return (this.permissions()?.projects ?? []).filter((entry) => {
      const current = checked[entry.projectId];
      return !!current && !samePermissions(current, effectivePermissions(entry));
    });
  });

  readonly tenantRoleOverrideHintKey = computed<string | null>(() => {
    const user = this.user();
    const roleId = this.roleIdSignal();
    if (!user || !roleId || roleId === user.roleId) return null;
    const hasOverrides = (this.permissions()?.projects ?? []).some(
      (entry) => entry.granted.length > 0 || entry.revoked.length > 0,
    );
    if (!hasOverrides) return null;
    const nextRole = this.roles().find((role) => role.id === roleId);
    return nextRole?.code === 'TENANT_ADMIN'
      ? 'userEdit.stammdaten.overrideHintTenantAdmin'
      : 'userEdit.stammdaten.overrideHint';
  });

  private readonly statusSignal = toSignal(this.form.controls.status.valueChanges, {
    initialValue: this.form.controls.status.value,
  });

  readonly statusPillKind = computed(() => {
    const status = this.statusSignal();
    if (this.user()?.expired === true || status === 'EXPIRED') return 'abgelaufen';
    if (status === 'ACTIVE') return 'aktiv';
    if (status === 'PENDING') return 'pending';
    return 'inaktiv';
  });

  readonly invitationResent = signal(false);
  readonly invitationResending = signal(false);

  /**
   * Hinter der Seite kann eine Einladung oder ein echter Benutzer stehen. Unterschieden wird ueber
   * die Einladungs-Gueltigkeit: nur Einladungs-Zeilen tragen ein expiresAt.
   *
   * Der Status taugt dafuer nicht, weil die Status-Auswahl dieser Seite auch fuer einen echten
   * Benutzer PENDING zulaesst — der bekaeme sonst einen Einladungs-Block samt Aktion zum erneuten
   * Versenden, die serverseitig ins Leere laeuft.
   */
  readonly showInvitationInfo = computed(() => !!this.user()?.expiresAt);

  private readonly emailAllowedDomains = signal<string[]>([]);
  readonly emailAllowedDomainsLabel = computed(() => this.emailAllowedDomains().join(', '));

  readonly invitationExpired = computed(() => {
    const u = this.user();
    return u?.status === 'EXPIRED' || u?.expired === true;
  });

  constructor() {
    warnBeforeUnload(() => this.hasUnsavedChanges);
  }

  ngOnInit(): void {
    const routeCustomerId = this.route.snapshot.paramMap.get('customerId');
    this.fromVerwaltung = routeCustomerId === null;
    this.userId.set(this.route.snapshot.paramMap.get('userId') ?? '');

    const requestedTab = this.route.snapshot.queryParamMap.get('tab');
    if (requestedTab === 'rolle' || requestedTab === 'praeferenzen' || requestedTab === 'daten') {
      this.activeTab.set(requestedTab);
    }

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.cdr.markForCheck());

    this.accountService
      .getProfile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (profile) => {
          this.currentUserRole.set(profile.role);
          this.currentUserId.set(profile.id);
          this.applyAccessLocks();
          // Aus der Verwaltung bearbeitet ein Mandanten-Admin Personen des eigenen Kunden.
          if (this.fromVerwaltung) this.loadPage(profile.customerId);
        },
        error: () => {
          this.currentUserRole.set(null);
          if (this.fromVerwaltung) this.showLoadError();
        },
      });

    if (routeCustomerId !== null) this.loadPage(routeCustomerId);
  }

  private loadPage(customerId: string): void {
    this.customerId.set(customerId);
    // Kundendaten und Einzelabfrage gibt es nur für Platform-Admins; jede 403 merkt sich der auth-error.interceptor.
    const customer = this.fromVerwaltung
      ? of(null)
      : this.customerDetailService.getCustomer(customerId).pipe(catchError(() => of(null)));
    forkJoin({
      customer,
      user: this.loadUser(customerId, this.userId()),
      roles: this.tenantUserService.getRoles(customerId).pipe(catchError(() => of([]))),
    }).subscribe({
      next: ({ customer, user, roles }) => {
        if (customer) this.customer.set(customer);
        this.roles.set(roles);
        if (user) {
          this.user.set(user);
          this.populateFormFromUser(user);
          this.loadPermissions();
        } else {
          this.error.set('userEdit.loadError');
        }
        this.loading.set(false);
      },
      error: () => this.showLoadError(),
    });
  }

  private showLoadError(): void {
    this.error.set('userEdit.loadError');
    this.loading.set(false);
  }

  private loadPermissions(keep: Readonly<Record<string, string[]>> = {}): void {
    if (this.showInvitationInfo()) {
      this.permissionsState.set('invitation');
      return;
    }
    this.permissionsState.set('loading');
    this.permissionsService
      .list(this.customerId(), this.userId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (permissions) => {
          this.permissions.set(permissions);
          this.checkedPermissions.set(
            Object.fromEntries(
              permissions.projects.map((entry) => [
                entry.projectId,
                keep[entry.projectId] ?? effectivePermissions(entry),
              ]),
            ),
          );
          const selected = this.selectedPermissionProject();
          if (!permissions.projects.some((entry) => entry.projectId === selected)) {
            this.selectedPermissionProject.set(permissions.projects[0]?.projectId ?? null);
          }
          this.permissionsState.set('ready');
        },
        error: (err: HttpErrorResponse) => {
          this.permissions.set(null);
          this.checkedPermissions.set({});
          this.permissionsState.set(err.status === 403 ? 'forbidden' : 'error');
        },
      });
  }

  private applyServerPermissions(entry: ProjectPermissionEntryDTO, sent: string[]): void {
    this.permissions.update((current) =>
      current
        ? {
            ...current,
            projects: current.projects.map((project) => (project.projectId === entry.projectId ? entry : project)),
          }
        : current,
    );
    this.checkedPermissions.update((checked) => {
      const current = checked[entry.projectId];
      const editedDuringSave = !!current && !samePermissions(current, sent);
      return editedDuringSave ? checked : { ...checked, [entry.projectId]: effectivePermissions(entry) };
    });
  }

  private populateFormFromUser(user: CustomerUserDTO): void {
    const { firstName, lastName } = this.resolveNameParts(user);
    this.form.patchValue({
      firstName,
      lastName,
      email: user.email ?? '',
      roleId: this.resolveRoleId(user),
      status: this.normalizeStatusForForm(user.status),
      language: this.storedLanguage(),
      timezone: this.storedTimezone(),
    });
    // Eine falsche Einladung wird widerrufen und neu versendet, nicht umgeschrieben (NEX-1350, NEX-1696).
    const invitationOnly = [this.form.controls.email, this.form.controls.language, this.form.controls.timezone];
    for (const control of invitationOnly) {
      if (this.showInvitationInfo()) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    }
    this.applyAccessLocks();
    this.form.markAsPristine();
    this.cdr.markForCheck();
  }

  /** Nach dem Profil und nach jedem Befüllen anwenden, weil das Befüllen E-Mail und Präferenzen wieder freigibt. */
  private applyAccessLocks(): void {
    if (this.currentUserRole() === null) return;
    const { firstName, lastName, email, roleId, status, language, timezone } = this.form.controls;
    if (this.masterDataReadOnly()) {
      for (const control of [firstName, lastName, email, status, language, timezone]) {
        control.disable({ emitEvent: false });
      }
    }
    if (this.tenantRoleLock() === null) {
      roleId.enable({ emitEvent: false });
    } else {
      roleId.disable({ emitEvent: false });
    }
  }

  /** EXPIRED is a read-only invitation display state, not a value the settable Status dropdown offers. */
  private normalizeStatusForForm(status?: TenantUserStatus): 'ACTIVE' | 'PENDING' | 'INACTIVE' {
    return status === 'EXPIRED' ? 'PENDING' : (status ?? 'ACTIVE');
  }

  private resolveRoleId(user: CustomerUserDTO): string {
    const byCode = this.roles().find((r) => r.code === user.role);
    if (byCode) return byCode.id;
    return user.roleId ?? '';
  }

  private resolveNameParts(user: CustomerUserDTO): { firstName: string; lastName: string } {
    const first = (user.firstName ?? '').trim();
    const last = (user.lastName ?? '').trim();
    if (first || last) return { firstName: first, lastName: last };
    const display = (user.displayName ?? '').trim();
    if (!display) return { firstName: '', lastName: '' };
    const space = display.indexOf(' ');
    if (space === -1) return { firstName: '', lastName: display };
    return { firstName: display.substring(0, space).trim(), lastName: display.substring(space + 1).trim() };
  }

  setActiveTab(tab: TabId): void {
    this.activeTab.set(tab);
  }

  onTabKeydown(event: KeyboardEvent, index: number): void {
    const length = this.tabs.length;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.setActiveTab(this.tabs[(index + 1) % length].id);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.setActiveTab(this.tabs[(index - 1 + length) % length].id);
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.setActiveTab(this.tabs[0].id);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.setActiveTab(this.tabs[length - 1].id);
    }
  }

  navigateBack(): void {
    if (this.fromVerwaltung) {
      void this.router.navigate(['/settings'], { queryParams: { bereich: 'benutzer' } });
      return;
    }
    void this.router.navigate(['/client-management', this.customerId()]);
  }

  get canSave(): boolean {
    // `invalid` statt `!valid`: Eine ganz gesperrte Form ist DISABLED, Einzelrechte bleiben speicherbar.
    if (this.form.invalid || this.saving()) return false;
    return this.hasUnsavedChanges;
  }

  get hasUnsavedChanges(): boolean {
    const u = this.user();
    if (!u || this.deleted()) return false;
    const changes = this.pendingChanges(u);
    return changes.role || this.userFieldsChanged(changes) || this.changedPermissionProjects().length > 0;
  }

  /** Everything the user PUT carries; the role is saved through its own endpoint. */
  private userFieldsChanged(changes: PendingChanges): boolean {
    return (
      changes.name || changes.status || changes.email !== null || changes.language !== null || changes.timezone !== null
    );
  }

  confirmDeactivate(): Observable<boolean> {
    this.showDeactivateConfirm.set(true);
    return this.deactivateConfirm$.pipe(take(1));
  }

  onDeactivateConfirmed(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(true);
  }

  onDeactivateCancelled(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(false);
  }

  private pendingChanges(u: CustomerUserDTO): PendingChanges {
    const { firstName, lastName, email, roleId, status, language, timezone } = this.form.getRawValue();
    // Gesperrte Stammdaten zählen nie: Der aus displayName geteilte Name wiche sonst ab, und der PUT endete mit 403.
    const masterData = !this.masterDataReadOnly();
    const editable = masterData && !this.showInvitationInfo();
    const nextEmail = this.normalizeEmail(email);
    const emailChanged = editable && nextEmail !== this.normalizeEmail(u.email);
    return {
      name:
        masterData &&
        (firstName.trim() !== (u.firstName ?? '').trim() || lastName.trim() !== (u.lastName ?? '').trim()),
      status: masterData && status !== this.normalizeStatusForForm(u.status),
      role: this.currentUserRole() !== null && this.tenantRoleLock() === null && !!roleId && roleId !== u.roleId,
      email: emailChanged ? nextEmail : null,
      language: editable && language !== this.storedLanguage() ? language : null,
      timezone: editable && timezone !== this.storedTimezone() ? timezone : null,
    };
  }

  /** Same normalization as the backend, so a pure upper/lower-case edit is not a change. */
  private normalizeEmail(email: string | undefined): string {
    return (email ?? '').trim().toLowerCase();
  }

  onSave(): void {
    if (!this.canSave) return;
    const customerId = this.customerId();
    const userId = this.userId();
    const u = this.user();
    if (!customerId || !userId || !u) return;

    const { firstName, lastName, roleId, status } = this.form.getRawValue();
    const changes = this.pendingChanges(u);
    const userChanged = this.userFieldsChanged(changes);
    const changedProjects = this.changedPermissionProjects();
    const catalog = this.permissions()?.catalog ?? [];
    const checked = this.checkedPermissions();

    if (!changes.role && !userChanged && changedProjects.length === 0) {
      this.form.markAsPristine();
      return;
    }

    this.saving.set(true);
    const request: UpdateCustomerUserRequest = { firstName, lastName, status };
    if (changes.email !== null) request.email = changes.email;
    if (changes.language !== null) request.language = changes.language;
    if (changes.timezone !== null) request.timezone = changes.timezone;
    const persistUser = userChanged
      ? this.tenantUserService
          .updateUser(customerId, userId, request)
          .pipe(map((updated) => this.applyServerUser(updated)))
      : of<CustomerUserDTO | null>(null);
    const persistPermissions = (input: CustomerUserDTO | null): Observable<CustomerUserDTO | null> =>
      from(changedProjects).pipe(
        concatMap((entry) =>
          this.permissionsService
            .update(customerId, userId, entry.projectId, overridesFor(entry, checked[entry.projectId], catalog))
            .pipe(
              catchError((err: HttpErrorResponse) =>
                throwError(
                  () => new PermissionSaveError(err.status, (err.error as { code?: string } | null)?.code ?? null),
                ),
              ),
              tap((updated) => this.applyServerPermissions(updated, checked[entry.projectId])),
            ),
        ),
        last(undefined, null),
        map(() => input),
      );
    const assignRole = this.isPlatformAdmin()
      ? () => this.tenantUserService.assignPlatformRole(customerId, userId, roleId)
      : () => this.tenantUserService.assignTenantRole(customerId, userId, roleId);
    const persistRole = (input: CustomerUserDTO | null): Observable<CustomerUserDTO | null> =>
      changes.role ? assignRole().pipe(map((updated) => this.applyServerUser(updated))) : of(input);

    const onlyPermissions = !userChanged && !changes.role;
    persistUser.pipe(concatMap(persistPermissions), concatMap(persistRole)).subscribe({
      next: () => {
        this.saving.set(false);
        if (changes.role) this.loadPermissions();
        const key = onlyPermissions ? 'userEdit.berechtigungen.saveSuccess' : 'userEdit.save.success';
        this.snackBar.open(this.translate.instant(key), undefined, { panelClass: 'snackbar-success' });
      },
      error: (err: HttpErrorResponse | PermissionSaveError) => {
        this.saving.set(false);
        if (err instanceof PermissionSaveError) {
          if (err.projectGone) this.loadPermissions(this.unsavedPermissions());
          const key = this.permissionSaveErrorKey(err, userChanged, changes.role);
          if (key) this.snackBar.open(this.translate.instant(key), undefined, { panelClass: 'snackbar-error' });
          return;
        }
        if (this.showServerEmailError(err)) return;
        let key = 'userEdit.save.errorGeneric';
        if (err.status === 403) key = 'userEdit.role.errorForbidden';
        else if (err.status === 400) key = 'userEdit.save.errorValidation';
        else if (err.status === 409) key = 'userEdit.role.errorLastAdmin';
        this.snackBar.open(this.translate.instant(key), undefined, { panelClass: 'snackbar-error' });
      },
    });
  }

  private permissionSaveErrorKey(err: PermissionSaveError, userSaved: boolean, roleChanged: boolean): string | null {
    // The projectArchivedInterceptor reports the reason and replaces any snackbar opened here.
    if (err.projectArchived) return null;
    if (userSaved) {
      return roleChanged
        ? 'userEdit.berechtigungen.saveErrorPartialWithRole'
        : 'userEdit.berechtigungen.saveErrorPartial';
    }
    if (err.status === 404) return 'userEdit.berechtigungen.saveErrorProjectGone';
    return PERMISSION_SAVE_ERROR_KEYS[err.status] ?? 'userEdit.berechtigungen.saveErrorGeneric';
  }

  private unsavedPermissions(): Record<string, string[]> {
    const checked = this.checkedPermissions();
    return Object.fromEntries(
      this.changedPermissionProjects().map((entry) => [entry.projectId, checked[entry.projectId]]),
    );
  }

  private showServerEmailError(err: HttpErrorResponse): boolean {
    const body = err.error as { code?: string; allowedDomains?: unknown } | null;
    const errorKey = body?.code ? EMAIL_ERROR_BY_CODE[body.code] : undefined;
    if (!errorKey) return false;
    if (body?.code === 'EMAIL_DOMAIN_NOT_ALLOWED') {
      this.emailAllowedDomains.set(Array.isArray(body.allowedDomains) ? body.allowedDomains.map(String) : []);
    }
    const email = this.form.controls.email;
    this.rejectedEmail = { value: email.value, errorKey };
    email.updateValueAndValidity();
    email.markAsTouched();
    this.activeTab.set('daten');
    this.cdr.markForCheck();
    afterNextRender(() => this.emailInput()?.nativeElement.focus(), { injector: this.injector });
    return true;
  }

  private applyServerUser(user: CustomerUserDTO): CustomerUserDTO {
    this.rejectedEmail = null;
    this.user.set(user);
    this.populateFormFromUser(user);
    return user;
  }

  openDeleteDialog(): void {
    this.blockingProjects.set([]);
    this.showDeleteDialog.set(true);
  }

  onDeleteConfirmed(): void {
    if (this.deleting()) return;
    const customerId = this.customerId();
    const userId = this.userId();
    if (!customerId || !userId) return;

    this.deleting.set(true);
    this.tenantUserService.delete(customerId, userId).subscribe({
      next: () => {
        this.deleting.set(false);
        this.showDeleteDialog.set(false);
        this.blockingProjects.set([]);
        this.snackBar.open(this.translate.instant('userEdit.delete.success'), undefined, {
          panelClass: 'snackbar-success',
        });
        this.deleted.set(true);
        this.navigateBack();
      },
      error: (err: HttpErrorResponse) => {
        this.deleting.set(false);
        if (err.status === 409) {
          const projects = (err.error?.blockingProjects as BlockingProject[] | undefined) ?? [];
          this.blockingProjects.set(projects);
          return;
        }
        const key = this.deleteErrorKey(err.status);
        this.snackBar.open(this.translate.instant(key), undefined, { panelClass: 'snackbar-error' });
        this.showDeleteDialog.set(false);
        this.blockingProjects.set([]);
      },
    });
  }

  onDeleteCancelled(): void {
    if (this.deleting()) return;
    this.showDeleteDialog.set(false);
    this.blockingProjects.set([]);
  }

  onResendInvitation(): void {
    const customerId = this.customerId();
    const user = this.user();
    if (!customerId || !user || this.invitationResent() || this.invitationResending()) return;

    this.invitationResending.set(true);
    this.tenantUserService.resendInvitation(customerId, user.id).subscribe({
      next: (invitation) => {
        this.invitationResending.set(false);
        this.switchToReplacementInvitation(customerId, user, invitation);
        if (invitation.mailDelivered === false) {
          this.snackBar.open(
            this.translate.instant('customerDetail.actions.resendMailNotDelivered'),
            undefined,
            MAIL_NOT_DELIVERED_SNACKBAR,
          );
          return;
        }
        this.invitationResent.set(true);
      },
      error: () => {
        this.invitationResending.set(false);
        this.snackBar.open(this.translate.instant('customerDetail.actions.resendError'), undefined, {
          panelClass: 'snackbar-error',
        });
      },
    });
  }

  // Der Resend ersetzt die Einladung durch eine neue mit eigener ID; ein weiterer Resend, Speichern
  // oder Entfernen muss sich auf diese beziehen. Die URL folgt, damit ein Reload sie wieder öffnet.
  private switchToReplacementInvitation(
    customerId: string,
    previous: CustomerUserDTO,
    replacement: InvitationDTO,
  ): void {
    this.userId.set(replacement.id);
    this.user.set({
      ...previous,
      id: replacement.id,
      status: 'PENDING',
      expiresAt: replacement.expiresAt,
      expired: false,
    });
    const target = this.fromVerwaltung
      ? ['/settings', 'benutzer', replacement.id, 'bearbeiten']
      : ['/client-management', customerId, 'users', replacement.id, 'bearbeiten'];
    void this.router.navigate(target, { replaceUrl: true, queryParamsHandling: 'preserve' });
    this.loadUser(customerId, replacement.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((reloaded) => {
        if (reloaded) this.user.set(reloaded);
      });
  }

  // Die Einzelabfrage kennt nur echte Benutzer und nur Platform-Admins; Einladungen stehen nur in der Benutzerliste.
  // Wer nicht in der Liste des eigenen Kunden steht, bleibt für Mandanten-Admins unerreichbar.
  private loadUser(customerId: string, userId: string): Observable<CustomerUserDTO | null> {
    const fromList = (): Observable<CustomerUserDTO | null> =>
      this.tenantUserService.getUsers(customerId).pipe(
        map((users) => users.find((u) => u.id === userId) ?? null),
        catchError(() => of(null)),
      );
    if (this.fromVerwaltung) return fromList();
    return this.tenantUserService.getUser(customerId, userId).pipe(catchError(fromList));
  }

  private deleteErrorKey(status: number): string {
    switch (status) {
      case 400:
        return 'userEdit.delete.errorSelf';
      case 403:
        return 'userEdit.delete.errorForbidden';
      case 404:
        return 'userEdit.delete.errorNotFound';
      default:
        return 'userEdit.delete.errorGeneric';
    }
  }
}
